import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import TextField from 'Shared/components/TextField/TextField';
import PrepopulatedTextField from 'Shared/components/PrepopulatedTextField/PrepopulatedTextField';
import { getOrganizationDomain } from 'Shared/selectors/organization';
import ConnectedCalendarIcon from 'Shared/components/ConnectedCalendarIcon/ConnectedCalendarIcon';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import WorkspaceTeammateSuggestions from '../WorkspaceTeammateSuggestions/WorkspaceTeammateSuggestions';

import './CreateTeammateField.scss';
import { WpIcon } from 'Wpds/components/icon';

type Props = {
  index: number;
  identif: string;
  formValues: {};
  valuesCount: number;
  isSuggestedListOpened: boolean;
  formValue: {
    fullName: string;
    email: string;
    recurringMeetingId: number;
    errorMessage: string;
  };
  onTextChange: Function;
  onRemoveItem: Function;
  onRemoveRecurringMeetingId?: Function;
  onSuggestionClick: Function;
  onSetIsSuggestedListOpened: Function;
};

function CreateTeammateField({
  index,
  identif,
  valuesCount,
  formValues,
  isSuggestedListOpened,
  formValue,
  onTextChange,
  onRemoveItem,
  onSuggestionClick,
  onSetIsSuggestedListOpened,
}: Props) {
  const { tr } = useTranslation();
  const [isNameFieldFocused, setIsNameFieldFocused] = useState(false);

  const organizationDomain = useSelector(getOrganizationDomain);

  const handleTextInputChange = event => onTextChange(event, identif);

  const handleTextInputChangeForFullName = event => {
    const newEvent = event;
    newEvent.target.name = 'fullName';
    handleTextInputChange(newEvent);
  };

  const handleRemoveItem = () => onRemoveItem(identif);

  const handleTextInputFocus = () => {
    setIsNameFieldFocused(true);
    onSetIsSuggestedListOpened(true);
  };

  const handleTextInputBlur = () =>
    setTimeout(() => {
      setIsNameFieldFocused(false);
      onSetIsSuggestedListOpened(true);
    }, 500);

  return (
    <div className="create-teammate-field">
      <div key={identif}>
        <div className="d-flex align-items-end">
          <TextField
            name={`fullName-${Math.random()}`}
            type="text"
            label={index === 0 ? tr('perform.workspace.simple_add_teammate.full_name_label') : null}
            labelMuted={false}
            placeholder={tr('perform.workspace.simple_add_teammate.full_name_placeholder')}
            wrapperClassName="mb-2 mr-2"
            className="bulk-invite-temmates-modal-input py-1"
            value={formValue.fullName}
            onChange={handleTextInputChangeForFullName}
            onFocus={handleTextInputFocus}
            onBlur={handleTextInputBlur}
            inputIcon={formValue.recurringMeetingId && <ConnectedCalendarIcon />}
          />

          <PrepopulatedTextField
            name="email"
            type="text"
            label={index === 0 ? tr('perform.workspace.simple_add_teammate.invite_to_workspace_optional_label') : null}
            labelMuted={false}
            placeholder={tr('perform.workspace.simple_bulk_invite.email_placeholder')}
            prepopulatedText={String(organizationDomain || '')}
            wrapperClassName="mb-2"
            className="bulk-invite-temmates-modal-input py-1"
            value={formValue.email}
            onChange={handleTextInputChange}
            onBlur={handleTextInputBlur}
          />
          {valuesCount > 1 ? (
            <span
              className={`bulk-invite-temmates-modal-delete-icon align-self-center ml-2 ${
                index === 0 ? 'mt-3' : 'mb-1'
              }`}
              onClick={handleRemoveItem}
            >
              <WpIcon prefix="fas" name="times-circle" />
            </span>
          ) : (
            <div className="mr-4" />
          )}
        </div>
        <WorkspaceTeammateSuggestions
          identif={identif}
          formValues={formValues}
          isSuggestedListOpened={isSuggestedListOpened && isNameFieldFocused}
          workspaceName={formValue.fullName}
          onSuggestionClick={onSuggestionClick}
        />
        {formValue.errorMessage && (
          <div className="alert alert-danger px-2 py-1 mb-2 small" role="alert">
            {formValue.errorMessage}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateTeammateField;
